import { OEP_EMAILS, type OEPEmailKey } from 'agu_oep_core';

/** Constant filter (no user input): the OEP templates are named 'OEP: <Key>' (metadata/templates/oep-*.json). */
export const OEP_TEMPLATE_FILTER = "TemplateID IN (SELECT ID FROM __mj.Template WHERE Name LIKE 'OEP:%')";
/** Implementer copy: a listed email whose template row was not returned. */
export const OEP_TEMPLATE_MISSING = 'This template has not been loaded.';

export interface OEPTemplateContentRow { TemplateID: string; TemplateText: string | null; Template: string }
export interface OEPEmailEntry { Key: OEPEmailKey; Trigger: string; Subject: string; Body: string }

const LINE_BREAK_AFTER = 'br, li, tr';
const PARAGRAPH_BREAK_AFTER = 'p, div, h1, h2, h3, h4, h5, h6, blockquote, ul, ol, table';

/**
 * Template HTML as plain text so staff read the copy as stored. DOMParser builds an inert document
 * (no script runs, nothing loads); only textContent is read, and the result is rendered with text
 * interpolation, never as HTML. Source whitespace collapses as HTML would; blocks become line breaks.
 */
export function HtmlToText(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('script, style, head').forEach((n) => n.remove());
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) n.textContent = (n.textContent ?? '').replace(/\s+/g, ' ');
    doc.body.querySelectorAll(LINE_BREAK_AFTER).forEach((el) => el.after(doc.createTextNode('\n')));
    doc.body.querySelectorAll(PARAGRAPH_BREAK_AFTER).forEach((el) => el.after(doc.createTextNode('\n\n')));
    return (doc.body.textContent ?? '')
        .split('\n').map((l) => l.trim()).join('\n')
        .replace(/\n{3,}/g, '\n\n').trim();
}

/** One entry per OEP_EMAILS key (trigger, subject as written with its {{ }} variables), body from the loaded template. */
export function BuildEmailEntries(rows: readonly OEPTemplateContentRow[]): OEPEmailEntry[] {
    const byName = new Map<string, string>();
    for (const r of rows) if (r.TemplateText !== null && !byName.has(r.Template)) byName.set(r.Template, r.TemplateText);
    return (Object.keys(OEP_EMAILS) as OEPEmailKey[]).map((Key) => {
        const def = OEP_EMAILS[Key];
        const html = byName.get(def.TemplateName);
        return { Key, Trigger: def.Trigger, Subject: def.Subject, Body: html === undefined ? OEP_TEMPLATE_MISSING : HtmlToText(html) };
    });
}
